import { createContext, useContext, useCallback, useEffect, useState, useMemo } from "react";
import {
  SALE_STATUS,
  ADD_TO_CART_OPTIONS,
  STOCK_CHECK_OPTIONS,
  EVENTS,
  TAX_PREFERENCES,
  DRAFT_SAVE_REASONS,
} from "const";
import { eventsEmitter } from "utils/events";
import TaxHelper from "helpers/TaxHelper";
import { nanoid } from "nanoid";
import { toast } from "react-hot-toast";
import CartHelper from "helpers/CartHelper";
import ProductContext from "./ProductContext";
import ConfigHelper from "helpers/ConfigHelper";
import * as SalesHelper from "helpers/SalesHelper";
import * as ProductHelper from "helpers/ProductHelper";
import * as ShiftHelper from "helpers/ShiftHelper";
import DateTimeHelpers from "helpers/DateTimeHelpers";
import * as DeletedCartHelper from "helpers/DeletedCartHelper";
import * as NumberFormatHelper from "helpers/NumberFormatHelper";
import OnlineOrderContext from "./OnlineOrderContext";
import WebSocketGatewayService from "services/WebSocketGatewayService";

const SalesContext = createContext();

export function SalesProvider(props) {
  const { items, priceCategoryItems, addonMappings, charges, chargeGroups } = useContext(ProductContext);
  const { updateOnlineOrderStatus } = useContext(OnlineOrderContext);

  const [sales, setSales] = useState([]);
  const [selectedPriceCategory, setSelectedPriceCategory] = useState(null);
  const [activeSale, setRawActiveSale] = useState(SalesHelper.getInitialSaleData());

  const activePriceCategory = useMemo(() => {
    return SalesHelper.getActiveSalePriceCategory(activeSale, selectedPriceCategory);
  }, [activeSale, selectedPriceCategory]);

  useEffect(() => {
    if (activeSale) {
      const activeCat = SalesHelper.getActiveSalePriceCategory(activeSale);
      if (activeCat && (!selectedPriceCategory || selectedPriceCategory.id !== activeCat.id)) {
        setSelectedPriceCategory(activeCat);
      }
    }
  }, [activeSale?.local_id, activeSale?.table?.id, activeSale?.customer?.id, activeSale, selectedPriceCategory]);

  /**
   * Normalizes a sale object when loaded or updated for editing in the POS.
   * If the sale has an active bill-level discount, it reverses the spread discount applied to individual item rates and restores them to their original (undiscounted) prices.
   * This enables editing quantities or adding/removing items at original rates.
   * The bill-level discount is preserved at the top level and automatically reapplied to the final calculated totals.
   *
   * @param {Object} sale - The sale object to process.
   * @returns {Object} The normalized sale object with restored rates and flat discount.
   */
  const normalizeSaleForEditing = useCallback(
    (sale) => {
      if (!sale) return sale;

      const existingDiscount = Number(sale.extraDiscount || 0);
      if (existingDiscount <= 0) {
        return { ...sale, ...SalesHelper.calculateDueAndChange(sale, sale.payable) };
      }

      const { selectedChargeGroupIds, saleLevelChargeGroupIds } = SalesHelper.getActiveChargeGroupIds(sale);

      const cleanedItems = (sale.items || []).map((saleItem) => {
        const product = saleItem.item;
        if (!product) return saleItem;

        const taxPreference = TaxHelper.getTaxPreference();
        const isExcludingTax = taxPreference === TAX_PREFERENCES.EXCLUDING_TAX;
        const taxPercentage = saleItem.taxPercentage ?? product.taxPercentage ?? 0;
        const cessPercentage = saleItem.cessPercentage ?? product.cessPercentage ?? 0;
        const totalTaxPercentage = Number(taxPercentage) + Number(cessPercentage);

        const originalRate = isExcludingTax
          ? (saleItem.rateAndTax ?? saleItem.rate) / (1 + totalTaxPercentage / 100)
          : (saleItem.rateAndTax ?? saleItem.rate);

        const data = CartHelper.getCartItemData(
          product,
          saleItem,
          saleItem.qty,
          {
            discountPercentage: 0,
            orderType: sale.order_type,
            rate: originalRate,
            selectedChargeGroupIds,
          },
          charges,
        );
        return { ...data, localId: saleItem.localId || nanoid() };
      });

      const totals = SalesHelper.calculateSaleSummary(cleanedItems, {
        charges,
        saleLevelChargeGroupIds,
        selectedChargeGroupIds,
        extraDiscount: existingDiscount,
      });

      const dueAndChange = SalesHelper.calculateDueAndChange(sale, totals.payable);

      return {
        ...sale,
        ...totals,
        ...dueAndChange,
        items: cleanedItems,
        extraDiscount: existingDiscount,
      };
    },
    [charges],
  );

  /**
   * Custom setter wrapper for activeSale state.
   * Intercepts updates to the active sale to ensure the data is normalized for editing before being placed in state.
   *
   * IMPORTANCE / ARCHITECTURAL RATIONALE:
   * Exposing a raw `useState` setter (e.g., setRawActiveSale) directly to components is avoided here.
   * This function acts as middleware, guaranteeing that any incoming sale object (whether loaded from
   * database history, selected from active tables, or passed from a dialog) goes through `normalizeSaleForEditing`.
   * Without this interceptor, developers would have to manually normalize sales before every call to set state,
   * which is highly error-prone and can lead to bugs where discount percentages, rates, and taxes are compounded.
   *
   * @param {Object} sale - The raw or updated sale object.
   */
  const setActiveSale = useCallback(
    (sale) => {
      setRawActiveSale(normalizeSaleForEditing(sale));
    },
    [normalizeSaleForEditing],
  );

  const chargeGroupsAppliedForSale = useMemo(() => {
    if (!activeSale) return [];

    if (activeSale.chargeGroupsCustomized) {
      return activeSale.selectedChargeGroups || [];
    }

    const { saleLevelChargeGroupIds } = SalesHelper.getActiveChargeGroupIds(activeSale);
    const saleLevelIdsSet = new Set((saleLevelChargeGroupIds || []).map(Number));

    return (chargeGroups || []).filter((g) => saleLevelIdsSet.has(Number(g.id)));
  }, [activeSale, chargeGroups]);

  const saleApplicableChargeOptions = useMemo(() => {
    const { selectedChargeGroupIds, saleLevelChargeGroupIds } = SalesHelper.getActiveChargeGroupIds(activeSale);

    return {
      charges,
      saleLevelChargeGroupIds,
      selectedChargeGroupIds,
      orderType: activeSale?.order_type,
      extraDiscount: activeSale?.extraDiscount,
    };
  }, [charges, activeSale]);

  ////// Updated sales handling functions ////////
  /**
   * Fetches the latest active sales from the local database and updates the global `sales` state.
   * If `localId` is provided, fetches and updates only that specific sale in the `sales` state.
   *
   * @param {string|number} [localId] - Optional local ID of a single sale to refresh.
   * @returns {Promise<Array|Object|null>}
   */
  const refreshSales = useCallback(async (localId) => {
    try {
      if (localId !== undefined && localId !== null) {
        const singleSale = await SalesHelper.getSaleById(localId);
        const isActive = SalesHelper.isSaleActive(singleSale);

        if (singleSale && isActive) {
          setSales((prev) => {
            const exists = prev.some((sale) => sale.local_id === localId);
            if (exists) {
              return prev.map((sale) => (sale.local_id === localId ? singleSale : sale));
            }
            return [...prev, singleSale];
          });
        } else {
          setSales((prev) => prev.filter((sale) => sale.local_id !== localId));
        }
        return singleSale;
      }

      const activeSales = await SalesHelper.getActiveSales();
      setSales(activeSales || []);
      return activeSales || [];
    } catch (err) {
      console.error("SalesContext#refreshSales error", err);
      return [];
    }
  }, []);

  /**
   * Initializes a brand new sale, overriding the active sale, and appending it to the global sales list.
   * @param {Object} data - Optional predefined data to seed the initial sale.
   * @returns {Object} The newly created local sale instance.
   */
  const createNewSale = useCallback(
    async (data = {}) => {
      const newSale = await SalesHelper.createNewSale(data, saleApplicableChargeOptions);
      await refreshSales();
      setActiveSale(newSale);
      return newSale;
    },
    [refreshSales, saleApplicableChargeOptions, setActiveSale],
  );

  /**
   * Fetches a specific sale by its ID from the local database.
   * @param {string|number} id - The local ID or bill number of the sale.
   * @returns {Promise<Object|null>} The sale object if found, otherwise null.
   */
  const getSaleById = useCallback(async (id) => {
    return await SalesHelper.getSaleById(id);
  }, []);

  /**
   * Main integration handler that accepts a product input and processes it into the active cart.
   * Runs stock validation, invokes SalesHelper modular addon handling, and triggers the cart state sync.
   * @param {Object} options - Destruction block containing product details:
   * @param {Object} options.product - The product object to add.
   * @param {number} [options.quantity=1] - Quantity value to add.
   * @param {string} [options.note] - Optional line item note.
   * @param {number|null} [options.rate=null] - Fixed override rate (null to use computed).
   * @param {Object|null} [options.option=null] - Selected item variation/option parameters.
   */
  const addProductToActiveSale = useCallback(
    async (product, { quantity = 1, note = null, option = null, rate = null } = {}) => {
      const priceCategoryToUse = activePriceCategory;
      const updateSale = SalesHelper.handleAddProductItemsToSale(
        activeSale,
        product,
        quantity,
        note,
        option,
        rate,
        priceCategoryToUse,
        priceCategoryItems,
        addonMappings,
        charges,
      );
      if (!updateSale) return;

      const saleLocalId = await SalesHelper.saveSaleInDB(updateSale);
      await refreshSales();
      setActiveSale({ ...updateSale, local_id: saleLocalId });
    },
    [activeSale, setActiveSale, addonMappings, activePriceCategory, priceCategoryItems, charges, refreshSales],
  );

  const reduceSaleItemFromActiveSale = useCallback(
    async (saleItemLocalId, { quantity = 1 } = {}) => {
      let saleItems = [...(activeSale.items || [])];
      const saleItem = saleItems.find((si) => si.localId === saleItemLocalId);
      if (!saleItem) return;

      saleItem.qty = saleItem.qty - quantity;
      const { deletedItems = activeSale.deletedItems ?? [] } = SalesHelper.handleSaleItemDeletedUpdates(
        saleItem,
        {},
        activeSale,
      );

      if (saleItem.qty <= 0) {
        saleItems = saleItems.filter((si) => si.localId !== saleItemLocalId);
      } else {
        SalesHelper.calculateSaleItemSummary(saleItem, {
          orderType: activeSale.order_type,
          charges,
          priceCategory: activePriceCategory,
          priceCategoryItems,
        });
      }

      const totals = SalesHelper.calculateSaleSummary(saleItems, saleApplicableChargeOptions);
      const dueAndChange = SalesHelper.calculateDueAndChange(activeSale, totals.payable);
      const updateSale = {
        ...activeSale,
        items: saleItems,
        ...totals,
        ...dueAndChange,
        deletedItems,
      };

      const saleLocalId = await SalesHelper.saveSaleInDB(updateSale);
      if (saleLocalId !== activeSale.local_id) {
        updateSale.local_id = saleLocalId;
      }
      await refreshSales();
      setActiveSale(updateSale);
    },
    [
      activeSale,
      setActiveSale,
      charges,
      refreshSales,
      saleApplicableChargeOptions,
      activePriceCategory,
      priceCategoryItems,
    ],
  );

  const increaseSaleItemInActiveSale = useCallback(
    async (saleItemLocalId, { quantity = 1 } = {}) => {
      let saleItems = [...(activeSale.items || [])];
      const saleItem = saleItems.find((si) => si.localId === saleItemLocalId);
      if (!saleItem) return;

      const updatedQuantity = NumberFormatHelper.quantityRoundOff(saleItem.qty + quantity);
      if (ConfigHelper.preventNegativeStockBilling() === STOCK_CHECK_OPTIONS.TERMINAL.value) {
        if (!CartHelper.isStockAvailableForSale(saleItem.item || saleItem, updatedQuantity)) {
          toast.error("Stock not available to add");
          return;
        }
      }

      saleItem.qty = updatedQuantity;
      if (saleItem.option) {
        saleItem.optionQty = NumberFormatHelper.quantityRoundOff(
          (saleItem.option?.quantityRatio ?? 1) * updatedQuantity,
        );
      }

      const { deletedItems = activeSale.deletedItems ?? [] } = SalesHelper.handleSaleItemDeletedUpdates(
        saleItem,
        {},
        activeSale,
      );

      SalesHelper.calculateSaleItemSummary(saleItem, {
        orderType: activeSale.order_type,
        charges,
        priceCategory: activePriceCategory,
        priceCategoryItems,
      });

      const totals = SalesHelper.calculateSaleSummary(saleItems, saleApplicableChargeOptions);
      const dueAndChange = SalesHelper.calculateDueAndChange(activeSale, totals.payable);
      const updateSale = {
        ...activeSale,
        items: saleItems,
        ...totals,
        ...dueAndChange,
        deletedItems,
      };

      const saleLocalId = await SalesHelper.saveSaleInDB(updateSale);
      if (saleLocalId !== activeSale.local_id) {
        updateSale.local_id = saleLocalId;
      }
      await refreshSales();
      setActiveSale(updateSale);
    },
    [
      activeSale,
      setActiveSale,
      charges,
      refreshSales,
      saleApplicableChargeOptions,
      activePriceCategory,
      priceCategoryItems,
    ],
  );

  const removeSaleItemFromActiveSale = useCallback(
    async (saleItemId) => {
      const saleItem = (activeSale?.items || []).find((si) => si.localId === saleItemId);

      if (!saleItem) return;
      reduceSaleItemFromActiveSale(saleItemId, { quantity: saleItem.qty });
    },
    [reduceSaleItemFromActiveSale, activeSale?.items],
  );

  /**
   * Creates a 'soft' local sale that is initialized purely in memory and set as active,
   * without persisting immediate records sequentially until manually triggered.
   * @param {Object} saleData - Optional seeding data for the soft sale.
   */
  const createNewSoftSale = useCallback(
    (saleData = {}) => {
      const priceCategory = saleData?.table?.area?.price_category || saleData?.price_category || null;
      const newSale = SalesHelper.getInitialSaleData({
        ...saleData,
        price_category: priceCategory,
      });

      if (priceCategory) {
        setSelectedPriceCategory(priceCategory);
      }
      setActiveSale(newSale);
    },
    [setActiveSale],
  );

  const resetActiveSale = useCallback(async () => {
    const newSale = SalesHelper.getInitialSaleData();
    setActiveSale(newSale);
  }, [setActiveSale]);

  const updateSaleKotStatus = useCallback(
    async (sale, newOrderStatus) => {
      await SalesHelper.updateSaleKotStatus(sale.local_id, newOrderStatus);

      // Update online order status async
      if (sale.online_order_id) {
        try {
          const mappedOnlineStatus = SalesHelper.getMappedOnlineStatus(newOrderStatus);
          updateOnlineOrderStatus(sale.online_order_id, mappedOnlineStatus);
        } catch (error) {
          console.error("Failed to update online order status:", error);
        }
      }

      // Syncing orders with latest data
      await refreshSales(sale.local_id);
    },
    [refreshSales, updateOnlineOrderStatus],
  );

  useEffect(() => {
    refreshSales();

    const handleExternalUpdate = (_saleData) => {
      // TODO: Update only the updated sale
      refreshSales();
    };

    const unsubscribeFn = eventsEmitter.on(EVENTS.SALES_UPDATED, handleExternalUpdate);
    return unsubscribeFn;
  }, [refreshSales]);

  ////// Old existing sales handling functions ////////

  // Effect to sync activeSale with latest background data
  useEffect(() => {
    if (!activeSale?.local_id) return;

    const latestDbSale = sales.find((s) => s.local_id === activeSale.local_id);
    if (!latestDbSale) return;

    if (
      activeSale.payable !== latestDbSale.payable ||
      activeSale.status !== latestDbSale.status ||
      JSON.stringify(activeSale.items) !== JSON.stringify(latestDbSale.items)
    ) {
      const itemsWithIds = (latestDbSale.items || []).map((item) => ({
        ...item,
        localId: item.localId || nanoid(),
      }));

      // Merge latest data but preserve in-memory activeSale properties (like order_type)
      const updatedSale = {
        ...activeSale,
        ...latestDbSale,
        items: itemsWithIds,
      };

      setActiveSale(updatedSale);
    }
  }, [
    sales,
    setActiveSale,
    activeSale?.local_id,
    activeSale?.status,
    activeSale?.items,
    activeSale?.payable,
    activeSale,
  ]);

  const deleteSale = async (id) => {
    await SalesHelper.deleteSale(id, { notify: true });
    await refreshSales();
  };

  const updateSaleStatus = useCallback(
    async (sale, newStatus) => {
      const updatedSale = { ...sale, status: newStatus };

      // Update DB only if already saved in DB
      if (sale.local_id) {
        await SalesHelper.saveSaleInDB(updatedSale, { notify: true });
        await refreshSales();
      }

      return updatedSale;
    },
    [refreshSales],
  );

  const addSaleItem = (prevSale, prevItems, itemRequest) => {
    const { item, rate = null, option = null, addNew = false } = itemRequest;
    const addToCartOption = ConfigHelper.getAddToCartOption();
    const preventNegativeStock = ConfigHelper.preventNegativeStockBilling() === STOCK_CHECK_OPTIONS.TERMINAL.value;
    const initialRate = rate ?? option?.price ?? item.price;

    let updatedItems = [...prevItems];
    let existingItemIndex = -1;

    if (!addNew && addToCartOption !== ADD_TO_CART_OPTIONS.NEW_ITEM.value) {
      existingItemIndex = updatedItems.findIndex((it) => it.item?.id === item.id && it.option?.label === option?.label);
    }

    const existingItem = existingItemIndex > -1 ? updatedItems[existingItemIndex] : null;
    const saleItem = SalesHelper.buildOrUpdateSaleItem(existingItem, itemRequest, initialRate);

    if (preventNegativeStock) {
      if (!CartHelper.isStockAvailableForSale(item, saleItem.qty)) {
        toast.error(`Stock not available for ${item.name}`);
        return prevItems;
      }
    }

    const saleItemRate = getSaleItemRate(saleItem, prevSale?.customer);
    const fullyCalculatedItem = CartHelper.getSaleItemData(
      prevSale,
      {
        ...saleItem,
        priceCategoryRate: saleItem?.manualRate == null && activePriceCategory ? saleItemRate : null,
        rate: saleItemRate,
      },
      charges,
    );

    if (existingItemIndex > -1) {
      updatedItems[existingItemIndex] = fullyCalculatedItem;
    } else {
      updatedItems.push(fullyCalculatedItem);
    }

    return updatedItems;
  };

  async function handleAddItemToSale(item, qty = 1, note, rate = null, option = null, config = {}) {
    const updatedItems = addSaleItem(
      activeSale,
      activeSale.items || [],
      { item, qty, note, rate, option, ...config },
      activeSale?.customer,
    );

    const totals = SalesHelper.calculateSaleSummary(updatedItems, saleApplicableChargeOptions);
    const dueAndChange = SalesHelper.calculateDueAndChange(activeSale, totals.payable);

    const newSale = {
      ...activeSale,
      items: updatedItems,
      ...totals,
      ...dueAndChange,
    };

    const saleLocalId = await SalesHelper.saveSaleInDB(newSale);
    await refreshSales();
    setActiveSale({ ...newSale, local_id: saleLocalId });
  }

  const handleBatchAddItemsToSale = async (itemsToAdd) => {
    if (!Array.isArray(itemsToAdd) || itemsToAdd.length === 0) return;

    let updatedItems = [...(activeSale.items || [])];

    itemsToAdd.forEach((itemRequest) => {
      updatedItems = addSaleItem(activeSale, updatedItems, itemRequest, activeSale?.customer);
    });

    const totals = SalesHelper.calculateSaleSummary(updatedItems, saleApplicableChargeOptions);
    const dueAndChange = SalesHelper.calculateDueAndChange(activeSale, totals.payable);

    const newSale = {
      ...activeSale,
      items: updatedItems,
      ...totals,
      ...dueAndChange,
    };

    const saleLocalId = await SalesHelper.saveSaleInDB(newSale);
    await refreshSales();
    setActiveSale({ ...newSale, local_id: saleLocalId });
  };

  const handleChangeSelectedCustomer = async (customer) => {
    if (customer?.group?.price_category) {
      setSelectedPriceCategory(customer.group.price_category);
    } else if (customer === null && activeSale?.customer?.group?.price_category) {
      setSelectedPriceCategory(null);
    }

    let itemsToUpdate = activeSale.items;

    if (customer && customer?.group && customer.group.price_category) {
      const updatedSale = updateSaleItemsForCustomerGroup(activeSale, customer, customer.group.price_category);
      itemsToUpdate = updatedSale.items;
    } else if (customer === null && activeSale?.customer && activeSale?.customer?.group?.price_category) {
      const updatedSale = updateSaleItemsForCustomerGroup(activeSale, null, null);
      itemsToUpdate = updatedSale.items;
    }

    const fullCustomer = customer
      ? {
          ...customer,
          mobile: customer.mobile || "",
        }
      : null;

    await updateActiveSaleItems(itemsToUpdate, { customer: fullCustomer });
  };

  const updateSaleItemsForCustomerGroup = (sale, customer, priceCategory = null) => {
    const categoryToUse = priceCategory ?? (customer?.group?.price_category || null);

    const updatedItems = sale.items.map((item) => {
      let effectivePrice = null;

      if (customer && customer?.group?.price_category) {
        effectivePrice = CartHelper.getApplicableCustomerPrice(
          item.productId || item.id,
          item.qty,
          customer,
          priceCategoryItems,
        );
      } else if (categoryToUse) {
        effectivePrice = CartHelper.getApplicableCategoryPrice(
          item.productId || item.id,
          item.qty,
          categoryToUse,
          priceCategoryItems,
        );
      }

      const saleItem = {
        ...item,
        priceCategoryRate: effectivePrice ?? null,
      };

      SalesHelper.calculateSaleItemSummary(saleItem, {
        orderType: sale.order_type,
        charges,
        priceCategory: categoryToUse,
        priceCategoryItems,
        rate: effectivePrice ?? saleItem?.option?.price ?? saleItem?.item?.price,
      });

      return saleItem;
    });

    return {
      ...sale,
      customer,
      items: updatedItems,
    };
  };

  function computeUpdatedSalePayload(saleItem, activeSale, additionalUpdates, charges) {
    const saleItemRate = getSaleItemRate(saleItem, activeSale?.customer);

    const updatedSaleItem = {
      ...saleItem,
      rate: saleItemRate,
    };

    if (saleItem?.manualRate == null && activePriceCategory) {
      updatedSaleItem.priceCategoryRate = saleItemRate;
    }

    const mergedAdditionalUpdates = SalesHelper.handleSaleItemDeletedUpdates(
      updatedSaleItem,
      additionalUpdates,
      activeSale,
    );

    const isExisting = activeSale.items.some((it) => it.localId === updatedSaleItem.localId);
    const updatedSaleItems = isExisting
      ? activeSale.items.map((it) => (it.localId === updatedSaleItem.localId ? updatedSaleItem : it))
      : [...activeSale.items, updatedSaleItem];

    const tempSaleForUpdate = {
      ...activeSale,
      ...mergedAdditionalUpdates,
      items: updatedSaleItems,
    };
    const { selectedChargeGroupIds, saleLevelChargeGroupIds } = SalesHelper.getActiveChargeGroupIds(tempSaleForUpdate);

    SalesHelper.calculateSaleItemSummary(updatedSaleItem, {
      orderType: activeSale.order_type,
      charges,
      selectedChargeGroupIds,
      rate: saleItemRate,
      priceCategory: activePriceCategory,
      priceCategoryItems,
    });

    const totals = SalesHelper.calculateSaleSummary(updatedSaleItems, {
      charges,
      saleLevelChargeGroupIds,
      selectedChargeGroupIds,
      orderType: tempSaleForUpdate.order_type,
    });

    const dueAndChange = SalesHelper.calculateDueAndChange(activeSale, totals.payable);

    return {
      ...activeSale,
      ...mergedAdditionalUpdates,
      items: updatedSaleItems,
      ...totals,
      ...dueAndChange,
      deletedItems: mergedAdditionalUpdates.deletedItems ?? activeSale.deletedItems ?? [],
      saleLevelChargeGroupIds,
      selectedChargeGroupIds,
    };
  }

  const saleItemUpdateHandler = async (saleItem, additionalUpdates = {}) => {
    const newSale = computeUpdatedSalePayload(saleItem, activeSale, additionalUpdates, charges);

    const saleLocalId = await SalesHelper.saveSaleInDB(newSale);
    if (saleLocalId !== activeSale.local_id) {
      newSale.local_id = saleLocalId;
    }
    await refreshSales();
    setActiveSale(newSale);
  };

  const getSaleItemRate = useCallback(
    (saleItem, customer) => {
      if (saleItem?.manualRate != null) {
        return saleItem.manualRate;
      }

      let effectivePrice = null;
      const productId = saleItem?.productId || saleItem?.id || saleItem?.item?.id;
      const qty = saleItem?.qty || 1;

      const priceCategoryToUse =
        customer?.group?.price_category ||
        selectedPriceCategory ||
        activeSale?.price_category ||
        activeSale?.table?.area?.price_category;

      if (priceCategoryToUse) {
        effectivePrice = CartHelper.getApplicableCategoryPrice(productId, qty, priceCategoryToUse, priceCategoryItems);
      }

      if (effectivePrice !== null) {
        return effectivePrice;
      }
      return TaxHelper.getSaleItemBaseRate(saleItem);
    },
    [priceCategoryItems, selectedPriceCategory, activeSale],
  );

  const handleSetItemDiscount = async (saleItem, discountPercentage) => {
    const product = await ProductHelper.getProductById(saleItem.productId);
    if (!product) {
      return;
    }

    const { selectedChargeGroupIds } = SalesHelper.getActiveChargeGroupIds(activeSale);

    const data = CartHelper.getCartItemData(
      product,
      saleItem,
      saleItem.qty,
      {
        discountPercentage,
        orderType: activeSale.order_type,
        selectedChargeGroupIds,
      },
      charges,
    );

    const existingSaleItems = [...(activeSale?.items || [])];
    const salesItemIndex = existingSaleItems.findIndex((i) => i.localId === saleItem.localId);
    if (salesItemIndex === -1) {
      return;
    }
    existingSaleItems[salesItemIndex] = data;
    updateActiveSaleItems(existingSaleItems);
  };

  const handleClearItemDiscount = (cartItem) => {
    handleSetItemDiscount(cartItem, 0);
  };

  const handleSetItemRate = async (saleItem, rate) => {
    const product = await ProductHelper.getProductById(saleItem.productId);
    if (!product) {
      return;
    }

    const { selectedChargeGroupIds } = SalesHelper.getActiveChargeGroupIds(activeSale);

    const data = CartHelper.getCartItemData(
      product,
      saleItem,
      saleItem.qty,
      {
        rate,
        orderType: activeSale.order_type,
        selectedChargeGroupIds,
      },
      charges,
    );

    const existingSaleItems = [...(activeSale?.items || [])];
    const salesItemIndex = existingSaleItems.findIndex((i) => i.localId === saleItem.localId);
    if (salesItemIndex === -1) {
      return;
    }
    existingSaleItems[salesItemIndex] = data;
    updateActiveSaleItems(existingSaleItems);
  };

  const handleClearItemRate = (cartItem) => {
    const updatingItem = CartHelper.getUpdadingItem(items, cartItem);
    handleSetItemRate(cartItem, updatingItem?.price);
  };

  const updateSalePricesByCategory = (priceCategory) => {
    if (!activeSale?.items?.length) return;

    const updatedItems = activeSale.items.map((saleItem) => {
      let effectivePrice = null;
      if (activeSale?.customer && activeSale.customer.group) {
        effectivePrice = CartHelper.getApplicableCustomerPrice(
          saleItem.productId || saleItem.id,
          saleItem.qty,
          activeSale.customer,
          priceCategoryItems,
        );
      } else if (priceCategory) {
        effectivePrice = CartHelper.getApplicableCategoryPrice(
          saleItem.productId,
          saleItem.qty,
          priceCategory,
          priceCategoryItems,
        );
      }

      const updatedSaleItem = {
        ...saleItem,
        priceCategoryRate: effectivePrice ?? null,
      };

      SalesHelper.calculateSaleItemSummary(updatedSaleItem, {
        orderType: activeSale.order_type,
        charges,
        priceCategory,
        priceCategoryItems,
        rate: effectivePrice ?? updatedSaleItem?.option?.price ?? updatedSaleItem?.item?.price,
      });

      return updatedSaleItem;
    });

    updateActiveSaleItems(updatedItems, { price_category: priceCategory });
  };

  const handleChangePriceCategory = (priceCategory) => {
    setSelectedPriceCategory(priceCategory);
    if (activeSale?.items.length === 0) return;
    updateSalePricesByCategory(priceCategory);
  };

  const addChargeGroupToActiveSale = useCallback(
    async (chargeGroup) => {
      if (!activeSale) return;

      let nextGroups = [];
      let customized = true;

      if (chargeGroup === null) {
        customized = false;
        nextGroups = [];
      } else {
        const currentChargeGroups = activeSale?.chargeGroupsCustomized
          ? activeSale?.selectedChargeGroups || []
          : chargeGroupsAppliedForSale;

        const isSelected = currentChargeGroups.some((g) => g.id === chargeGroup.id);
        if (isSelected) {
          nextGroups = currentChargeGroups.filter((g) => g.id !== chargeGroup.id);
        } else {
          nextGroups = [...currentChargeGroups, chargeGroup];
        }
      }

      const sale = {
        ...activeSale,
        selectedChargeGroups: nextGroups,
        chargeGroupsCustomized: customized,
      };

      const { selectedChargeGroupIds, saleLevelChargeGroupIds } = SalesHelper.getActiveChargeGroupIds(sale);

      const updatedItems = await SalesHelper.recalculateSaleItems(sale, selectedChargeGroupIds, charges);

      const totals = SalesHelper.calculateSaleSummary(updatedItems, {
        charges,
        saleLevelChargeGroupIds,
        orderType: sale.order_type,
        extraDiscount: sale?.extraDiscount,
      });

      const dueAndChange = SalesHelper.calculateDueAndChange(sale, totals.payable);

      const newSale = {
        ...sale,
        ...totals,
        ...dueAndChange,
        items: updatedItems,
        saleLevelChargeGroupIds,
        selectedChargeGroupIds,
      };

      await SalesHelper.saveSaleInDB(newSale);
      await refreshSales();
      setActiveSale(newSale);
    },
    [activeSale, setActiveSale, charges, refreshSales, chargeGroupsAppliedForSale],
  );

  const updateActiveSaleItems = async (updatedItems, additionalUpdates = {}) => {
    const tempSaleForSummary = {
      ...activeSale,
      ...additionalUpdates,
      items: updatedItems,
    };
    const { selectedChargeGroupIds, saleLevelChargeGroupIds } = SalesHelper.getActiveChargeGroupIds(tempSaleForSummary);

    const totals = SalesHelper.calculateSaleSummary(updatedItems, {
      charges,
      saleLevelChargeGroupIds,
      selectedChargeGroupIds,
      orderType: tempSaleForSummary.order_type,
      extraDiscount: tempSaleForSummary?.extraDiscount,
    });

    const dueAndChange = SalesHelper.calculateDueAndChange(activeSale, totals.payable);

    const newSale = {
      ...activeSale,
      ...additionalUpdates,
      items: updatedItems,
      ...totals,
      ...dueAndChange,
      deletedItems: additionalUpdates.deletedItems ?? activeSale.deletedItems ?? [],
      saleLevelChargeGroupIds,
      selectedChargeGroupIds,
    };

    if (newSale.local_id) {
      await SalesHelper.saveSaleInDB(newSale);
      await refreshSales();
    }
    setActiveSale(newSale);
  };

  const handleSaveDraftSale = async (saleUpdates = {}, shouldClear = true) => {
    try {
      const saleSource = { ...(activeSale || {}), ...saleUpdates };

      if (!saleSource?.items || saleSource.items.length === 0) {
        await resetActiveSale();
        return { saved: false, reason: DRAFT_SAVE_REASONS.EMPTY_ITEMS };
      }

      const dueAndChange = SalesHelper.calculateDueAndChange(saleSource, saleSource.payable);

      const saleToPush = {
        ...saleSource,
        time: saleSource.time ?? DateTimeHelpers.getLocalISOString(),
        status: SALE_STATUS.DRAFT,
        order_type: saleSource?.order_type,
        payment_modes: saleSource.payment_modes || [],
        deletedItems: saleSource.deletedItems || [],
        ...dueAndChange,
      };

      if (saleToPush.local_id) {
        const existing = sales.find((sale) => String(sale.local_id) === String(saleToPush.local_id));
        await SalesHelper.saveSaleInDB({ ...(existing || {}), ...saleToPush }, { notify: true });
      } else {
        const local_id = await SalesHelper.createNewSaleInDB(saleToPush, { notify: true });
        saleToPush.local_id = local_id;
      }

      await refreshSales();

      if (shouldClear) {
        setSelectedPriceCategory(null);
        await resetActiveSale();
      } else {
        setActiveSale(saleToPush);
      }
      return { saved: true, local_id: saleToPush.local_id };
    } catch (error) {
      console.error("Error saving draft sale:", error);
      return { saved: false, error };
    }
  };

  const handleClearActiveSale = useCallback(async () => {
    setSelectedPriceCategory(null);
    await resetActiveSale();
  }, [resetActiveSale]);

  const handleDeleteSale = async (localId, description = "") => {
    const sale = sales.find((sale) => String(sale.local_id) === String(localId));

    if (!sale) return;

    // Log the deleted cart
    try {
      const shiftObj = ShiftHelper.getCurrentShiftId();
      await DeletedCartHelper.saveDeletedCart(sale, shiftObj.id, description);
    } catch (err) {
      console.error("handleDeleteSale: failed to log deletion", err);
    }

    await deleteSale(sale.local_id);
    if (activeSale?.local_id === sale.local_id) {
      resetActiveSale();
    }
  };

  const handleChangeSaleTable = async (saleLocalId, newTable, chair = null) => {
    if (!saleLocalId) return;

    const tableChair = chair ? { id: chair.id, name: chair.name } : newTable?.chair || null;

    const tableObj = {
      id: newTable.id,
      name: newTable.name,
      area: {
        id: newTable?.area?.id,
        name: newTable?.area?.name,
        price_category: newTable?.area?.price_category,
      },
      chair: tableChair,
    };

    const saleToUpdate = sales.find((sale) => (sale?.local_id ?? "") === saleLocalId);

    if (!saleToUpdate) {
      console.error(`handleChangeSaleTable: Sale with local_id ${saleLocalId} not found.`);
      return null;
    }
    const newPriceCategory = newTable?.area?.price_category || null;
    saleToUpdate.table = tableObj;
    saleToUpdate.price_category = newPriceCategory;

    if (saleToUpdate.items && saleToUpdate.items.length > 0) {
      saleToUpdate.items = saleToUpdate.items.map((saleItem) => {
        const cloned = { ...saleItem };
        SalesHelper.calculateSaleItemSummary(cloned, {
          orderType: saleToUpdate.order_type,
          charges,
          priceCategory: newPriceCategory,
          priceCategoryItems,
        });
        return cloned;
      });
      const { selectedChargeGroupIds, saleLevelChargeGroupIds } = SalesHelper.getActiveChargeGroupIds(saleToUpdate);
      const totals = SalesHelper.calculateSaleSummary(saleToUpdate.items, {
        charges,
        saleLevelChargeGroupIds,
        selectedChargeGroupIds,
        orderType: saleToUpdate.order_type,
        extraDiscount: saleToUpdate?.extraDiscount,
      });
      const dueAndChange = SalesHelper.calculateDueAndChange(saleToUpdate, totals.payable);
      Object.assign(saleToUpdate, totals, dueAndChange);
    }

    await SalesHelper.saveSaleInDB(saleToUpdate);
    await refreshSales();
    WebSocketGatewayService.broadcast({ action: "TABLE_CHANGED" });

    if (activeSale?.local_id === saleLocalId) {
      setSelectedPriceCategory(newPriceCategory);
      setActiveSale(saleToUpdate);
    }

    return saleToUpdate;
  };

  const handleSetCustomer = async (customer) => {
    const newSale = {
      ...activeSale,
      customer,
    };

    if (newSale.local_id) {
      await SalesHelper.saveSaleInDB(newSale);
      await refreshSales();
    }
    setActiveSale(newSale);
  };

  const handleSetSalesPerson = async (salesPerson) => {
    const newSale = {
      ...activeSale,
      waiter: salesPerson,
    };

    if (newSale.local_id) {
      await SalesHelper.saveSaleInDB(newSale, { notify: true });
      await refreshSales();
    }
    setActiveSale(newSale);
  };

  const handleSetDate = async (date) => {
    const newSale = {
      ...activeSale,
      time: date,
    };

    if (newSale.local_id) {
      await SalesHelper.saveSaleInDB(newSale);
      await refreshSales();
    }
    setActiveSale(newSale);
  };

  const handleSplitOrder = async (originalSale, itemsToSplit) => {
    if (!originalSale || !itemsToSplit || itemsToSplit.length === 0) return;

    try {
      const saleId = originalSale.local_id;
      const { original: updatedOriginal } = await SalesHelper.splitSale(saleId, itemsToSplit, { charges });

      if (activeSale && activeSale.local_id === originalSale.local_id) {
        setActiveSale(updatedOriginal);
      }
      await refreshSales();
    } catch (err) {
      console.error("Split order failed", err);
    }
  };

  const handleMergeSales = async (salesToMerge, targetTable) => {
    if (!salesToMerge || salesToMerge.length < 2) return false;

    try {
      const mergedSale = await SalesHelper.mergeSales(salesToMerge, {
        targetTable,
        charges,
        notify: false,
      });

      const mergedSalesIds = new Set(salesToMerge.map((s) => String(s.local_id)));
      if (activeSale && mergedSalesIds.has(String(activeSale.local_id))) {
        setActiveSale(mergedSale);
      }

      await refreshSales();
      return true;
    } catch (err) {
      console.error("Merge sales failed", err);
      return false;
    }
  };

  return (
    <SalesContext.Provider
      value={{
        sales,
        activeSale,
        selectedPriceCategory,
        handleChangeSelectedCustomer,
        setActiveSale,
        refreshSales,
        deleteSale,
        handleAddItemToSale,
        handleBatchAddItemsToSale,
        handleSetCustomer,
        handleSetSalesPerson,
        handleSetDate,
        handleSaveDraftSale,
        handleClearActiveSale,
        handleSetItemDiscount,
        handleClearItemDiscount,
        handleSetItemRate,
        handleClearItemRate,
        handleDeleteSale,
        handleChangeSaleTable,
        handleSplitOrder,
        getSaleItemRate,
        saleItemUpdateHandler,
        handleChangePriceCategory,
        handleMergeSales,
        updateSaleStatus,

        /// New sales handling functions ///
        createNewSale,
        addProductToActiveSale,
        increaseSaleItemInActiveSale,
        reduceSaleItemFromActiveSale,
        removeSaleItemFromActiveSale,
        createNewSoftSale,
        getSaleById,
        addChargeGroupToActiveSale,
        chargeGroupsAppliedForSale,
        updateSaleKotStatus,
      }}
    >
      {props.children}
    </SalesContext.Provider>
  );
}

export default SalesContext;
